import type { C1_FurnitureClipboard } from "./C1_FurnitureClipboard";
import type { C1_Room } from "./C1_Room";
import type { DropPreview } from "../types/C1_AppTypes";
import type Button from "../../../src/models/Button";
import type Consumer from "../../../src/models/Consumer";
import { placeExistingElectricalDevice } from "./C1_placeExistingElectricalDevice";
import { generateTags } from "../estada/C1_generateTags";
import { c1_state } from "./C1_runtimeContext";
import { itx } from "../../../src/translationItx/itx";

/** Deserialize the complete native device graph only after the user clicks a valid placement. */
export async function placeNativeClipboard(clipboard: C1_FurnitureClipboard, room: C1_Room,
  preview: Extract<DropPreview, { type: "furniture" }>) {
  if (!clipboard.native || !room.owner) throw new Error("Missing copied device or target room.");
  const rect = room.innerRect();
  if (rect.x2 <= rect.x1 || rect.y2 <= rect.y1) throw new Error("Cannot paste into a room without usable dimensions.");
  const stored = { data: state.clipboard.getData(), className: state.clipboard.getClassName() };
  const undo = state.undoManager.beginDialogStep(itx("c1.clipboard.pasteElement", "Element einfügen"));
  try {
    state.clipboard.setData(clipboard.native.data, clipboard.native.className);
    const items = await state.undoManager.trackAsyncWork(state.menubarActions.onPaste(room.owner));
    const device = items?.find(item => item.className === clipboard.native!.className && item.parent === room.owner) as Button | Consumer | undefined;
    if (!device) throw new Error("The copied device could not be created.");
    const placed = placeExistingElectricalDevice(room, device, {
      ...clipboard.data,
      side: preview.side, _wallId: preview.wallId, wallDocked: preview.wallDocked,
      reflect: preview.reflect ?? clipboard.data.reflect,
      relativePosition: { x: (preview.x - rect.x1) / (rect.x2 - rect.x1), y: (preview.y - rect.y1) / (rect.y2 - rect.y1) },
      baseProps: { ...clipboard.data.baseProps, rotation: preview.rotation },
    });
    device.name = clipboard.native.name;
    generateTags(c1_state.project);
    await undo.commit();
    return placed;
  } catch (error) {
    await undo.rollback(); throw error;
  } finally {
    if (stored.data) state.clipboard.setData(stored.data, stored.className ?? "");
    else state.clipboard.clear();
  }
}
